import { Injectable, NgZone } from '@angular/core';
import { loadCKEditorCloud } from '@ckeditor/ckeditor5-angular';
import type { ClassicEditor, EditorConfig } from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';
import { buildCKEditorConfig } from './ckeditor-config';
import { clearContentStyles } from './ckeditor-content-style-plugin';
import { ConfigServiceService } from '../../config/shared/config-service.service';

export const CKEDITOR_VERSION = '48.1.1';

type PooledEditor = ClassicEditor;

interface Bootstrap {
  Editor: typeof ClassicEditor;
  config: EditorConfig;
}

/**
 * A single host's exclusive use of a pooled editor. Every listener registered through the lease
 * is removed when the lease is released, so nothing leaks to the next host.
 */
export class CkEditorLease {
  private _listeners: Array<{ target: any; event: string; callback: Function }> = [];
  private _releaseCallbacks: Array<() => void> = [];
  private _isSettingData = false;
  public released = false;
  public readonly lockId: string;

  constructor(
    public readonly editor: PooledEditor,
    id: number,
    private _ngZone: NgZone
  ) {
    this.lockId = `wl-ckeditor-lease-${id}`;
  }

  /** Moves the editor UI into the host's container. */
  public attach(container: HTMLElement): void {
    container.appendChild(this.editor.ui.element!);
  }

  public getData(): string {
    return this.editor.data.get();
  }

  /** Replaces the editor content without creating an undo step or emitting a model change. */
  public setData(html: string): void {
    this._isSettingData = true;
    try {
      this.editor.data.set(html ?? '', { batchType: { isUndoable: false } });
    } finally {
      this._isSettingData = false;
    }
    clearUndoHistory(this.editor);
  }

  public setReadOnly(readOnly: boolean): void {
    if (readOnly) {
      this.editor.enableReadOnlyMode(this.lockId);
    } else {
      this.editor.disableReadOnlyMode(this.lockId);
    }
  }

  /** Registers a listener on the editor (or one of its emitters) that is removed on release. */
  public listen(target: any, event: string, callback: Function, options?: any): void {
    target.on(event, callback, options);
    this._listeners.push({ target, event, callback });
  }

  /** Model data changes caused by the user (not by setData), re-entered into the Angular zone. */
  public onChange(callback: (data: string, evt: any) => void): void {
    this.listen(this.editor.model.document, 'change:data', (evt: any) => {
      if (this._isSettingData) return;
      this._ngZone.run(() => callback(this.editor.data.get(), evt));
    });
  }

  public onFocus(callback: (evt: any) => void): void {
    this.listen(this.editor.editing.view.document, 'focus', (evt: any) => this._ngZone.run(() => callback(evt)));
  }

  public onBlur(callback: (evt: any) => void): void {
    this.listen(this.editor.editing.view.document, 'blur', (evt: any) => this._ngZone.run(() => callback(evt)));
  }

  /** Registers cleanup to run when the lease is released, before the editor is reset and parked. */
  public onRelease(callback: () => void): void {
    this._releaseCallbacks.push(callback);
  }

  /** @internal */
  public _runReleaseCallbacks(): void {
    this._releaseCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (e) {
        console.error('[ckeditor] release cleanup failed', e);
      }
    });
    this._releaseCallbacks = [];
  }

  /** @internal */
  public _removeListeners(): void {
    this._listeners.forEach(({ target, event, callback }) => target.off(event, callback));
    this._listeners = [];
  }
}

/**
 * CKEditor 5 bills per editor initialization, so editors are pooled instead of created per component.
 * Nothing is loaded until a host first needs an editor; editors are never destroyed for the life of
 * the SPA, and a released editor is parked off-screen until the next host acquires it.
 */
@Injectable({ providedIn: 'root' })
export class CkEditorPoolService {
  private _bootstrap: Promise<Bootstrap> | null = null;
  private _idle: PooledEditor[] = [];
  private _parkingLot: HTMLElement | null = null;
  private _leaseCount = 0;

  /** Number of editors created (billable loads) in this session. */
  public loadCount = 0;

  constructor(
    private _configService: ConfigServiceService,
    private _ngZone: NgZone
  ) {}

  /** Editors currently attached to a host. */
  public get inUseCount(): number {
    return this.loadCount - this._idle.length;
  }

  public async acquire(): Promise<CkEditorLease> {
    const reused = this._idle.pop();
    const editor = reused ?? (await this._createEditor());
    this._logPoolSize(reused ? 'acquire (reused)' : 'acquire (new load)');
    return new CkEditorLease(editor, ++this._leaseCount, this._ngZone);
  }

  public release(lease: CkEditorLease | null | undefined): void {
    if (!lease || lease.released) return;
    lease.released = true;
    lease._runReleaseCallbacks();
    lease._removeListeners();

    const editor = lease.editor;
    // ATLAS-5793: drop the owned content <style> before the next host's data arrives
    try {
      clearContentStyles(editor);
    } catch (e) {
      console.error('[ckeditor] failed to clear content styles', e);
    }
    try {
      exitTransientModes(editor);
      editor.disableReadOnlyMode(lease.lockId);
      editor.data.set('', { batchType: { isUndoable: false } });
      clearUndoHistory(editor);
    } catch (e) {
      console.error('[ckeditor] failed to reset pooled editor', e);
    }
    this._getParkingLot().appendChild(editor.ui.element!);
    this._idle.push(editor);
    this._logPoolSize('release');
  }

  private _load(): Promise<Bootstrap> {
    if (!this._bootstrap) {
      this._bootstrap = Promise.all([this._loadCloud(), this._configService.getCkeditor5License()]).then(([cloud, licenseKey]) =>
        this._buildConfig(cloud, licenseKey)
      );
      this._bootstrap.catch(() => (this._bootstrap = null));
    }
    return this._bootstrap;
  }

  /** @internal overridable for tests */
  protected _loadCloud() {
    return loadCKEditorCloud({ version: CKEDITOR_VERSION, premium: true });
  }

  /** @internal overridable for tests */
  protected _buildConfig(cloud: any, licenseKey: string): Bootstrap {
    return buildCKEditorConfig(cloud, licenseKey, () => this._configService.getCkeditor5Token());
  }

  private async _createEditor(): Promise<PooledEditor> {
    const { Editor, config } = await this._load();
    const sourceElement = document.createElement('div');
    this._getParkingLot().appendChild(sourceElement);
    const editor = await this._ngZone.runOutsideAngular(() => Editor.create({ ...config, attachTo: sourceElement }));
    this.loadCount++;
    return editor;
  }

  private _logPoolSize(action: string): void {
    console.info(`[ckeditor pool] ${action} — size: ${this.loadCount}, in use: ${this.inUseCount}, idle: ${this._idle.length}`);
  }

  private _getParkingLot(): HTMLElement {
    if (!this._parkingLot) {
      this._parkingLot = document.createElement('div');
      this._parkingLot.className = 'wl-ckeditor-parking-lot';
      this._parkingLot.style.display = 'none';
      this._parkingLot.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this._parkingLot);
    }
    return this._parkingLot;
  }
}

function clearUndoHistory(editor: PooledEditor): void {
  (editor.commands.get('undo') as any)?.clearStack();
  (editor.commands.get('redo') as any)?.clearStack();
}

/** Leaves fullscreen and source-editing modes so the next host gets a normal editor. */
function exitTransientModes(editor: PooledEditor): void {
  const fullscreen = editor.commands.get('toggleFullscreen');
  if (fullscreen?.value) {
    editor.execute('toggleFullscreen');
  }
  for (const pluginName of ['SourceEditing', 'SourceEditingEnhanced']) {
    const plugin: any = editor.plugins.has(pluginName) ? editor.plugins.get(pluginName) : null;
    if (plugin?.isSourceEditingMode) {
      plugin.isSourceEditingMode = false;
    }
  }
}
